import React, { useState, useEffect, useContext } from 'react';
import { CampaignProps } from './types/propTypes';
import { useHttp } from 'src/hooks';
import { AuthContext } from 'src/contexts';

const GenerateHeadline: React.FC<CampaignProps> = ({
    setCampaignData,
    campaignData,
    setIsStepValid,
}) => {
    const [editingField, setEditingField] = useState<string | null>(null);
    const [tempText, setTempText] = useState('');
    const [currentField, setCurrentField] = useState<string | null>(null);
    const { sendRequest, isLoading, error } = useHttp();
    const { accessToken } = useContext(AuthContext);

    // Use campaignData.headlines and primaryText
    const [content, setContent] = useState({
        headline1: campaignData.headlines[0] || "",
        headline2: campaignData.headlines[1] || "",
        primaryText: campaignData.primaryText || ""
    });

    // Update local content when campaignData changes
    useEffect(() => {
        setContent({
            headline1: campaignData.headlines[0] || "",
            headline2: campaignData.headlines[1] || "",
            primaryText: campaignData.primaryText || ""
        });
    }, [campaignData.headlines, campaignData.primaryText]);

    const handleRegenerate = async (field: string) => {
        setCurrentField(field);

        sendRequest(
            {
                url: 'campaign/generate-content',
                method: 'POST',
                payload: {
                    offeredProductsServices: campaignData.offeredProductsServices,
                    competitiveAdvantages: campaignData.competitiveAdvantages,
                    offers: campaignData.offers,
                    serviceDescription: campaignData.serviceDescription,
                    businessName: campaignData.businessName,
                    regenerate: true,
                },
                accessToken,
            },
            (data: any) => {
                setCurrentField(null);
                if (!data?.success) {
                    console.error('Regenerate failed');
                    return;
                }

                if (field === 'primaryText') {
                    const newPrimaryText = data.primaryText || '';
                    setContent(prev => ({ ...prev, primaryText: newPrimaryText }));
                    setCampaignData(prev => ({ ...prev, primaryText: newPrimaryText }));
                } else {
                    const index = field === 'headline1' ? 0 : 1;
                    const newHeadline = data.headlines?.[index] || '';
                    const updatedHeadlines = [...campaignData.headlines];
                    updatedHeadlines[index] = newHeadline;

                    setContent(prev => ({
                        ...prev,
                        [field]: newHeadline
                    }));
                    setCampaignData(prev => ({
                        ...prev,
                        headlines: updatedHeadlines
                    }));
                }
            }
        );
    };

    const handleEditClick = (field: string) => {
        setEditingField(field);
        setTempText(content[field as keyof typeof content]);
    };

    const handleSaveEdit = (field: string) => {
        const newContent = { ...content, [field]: tempText };
        setContent(newContent);

        if (field === 'primaryText') {
            setCampaignData(prev => ({
                ...prev,
                primaryText: tempText
            }));
        } else {
            const index = field === 'headline1' ? 0 : 1;
            const updatedHeadlines = [...campaignData.headlines];
            updatedHeadlines[index] = tempText;
            setCampaignData(prev => ({
                ...prev,
                headlines: updatedHeadlines
            }));
        }

        setEditingField(null);
    };

    const handleCancelEdit = () => {
        setEditingField(null);
    };

    // Loading animation component
    const LoadingAnimation = () => (
        <div className="flex items-center gap-2 text-[#6277FF]">
            <div className="flex space-x-1">
                <div className="w-2 h-2 rounded-full bg-[#6277FF] animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 rounded-full bg-[#6277FF] animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 rounded-full bg-[#6277FF] animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
            <span className="font-['Inter'] font-normal text-xl">AI is generating...</span>
        </div>
    );

    return (
        <div className="box-border flex flex-col items-center p-6 gap-10 w-[1115px] border border-[#E5E7E7] rounded-xl flex-none order-1 self-stretch flex-grow-0">
            {/* Header Section */}
            <div className="flex flex-col items-center gap-0.5 w-[1067px]">
                <h2 className="font-['Figtree'] font-semibold text-[28px] leading-[34px] text-center w-full">
                    Review & launch
                </h2>
            </div>

            {/* Headline Texts Section */}
            <div className="flex flex-col gap-8 w-[1067px]">
                {/* First Headline Block */}
                <div className="flex flex-col gap-3">
                    <h3 className="font-['Inter'] font-semibold text-xl leading-[150%]">
                        Headline texts
                    </h3>

                    {editingField === 'headline1' ? (
                        <div className="flex flex-col gap-2">
                            <textarea
                                value={tempText}
                                onChange={(e) => setTempText(e.target.value)}
                                className="border p-2 rounded"
                            />
                            <div className="flex gap-2">
                                <button
                                    onClick={() => handleSaveEdit('headline1')}
                                    className="px-4 py-1.5 bg-green-500 text-white rounded text-sm"
                                >
                                    Save
                                </button>
                                <button
                                    onClick={handleCancelEdit}
                                    className="px-4 py-1.5 bg-gray-300 rounded text-sm"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            {isLoading && currentField === 'headline1' ? (
                                <LoadingAnimation />
                            ) : (
                                <p className="font-['Inter'] font-normal text-xl leading-[150%] text-[#4A4D4F]">
                                    {content.headline1 || <span className="text-gray-400">Enter headline 1</span>}
                                </p>
                            )}
                            <div className="flex gap-2.5 w-[331px]">
                                <button
                                    onClick={() => handleRegenerate('headline1')}
                                    disabled={isLoading && currentField === 'headline1'}
                                    className={`px-6 py-2 rounded-lg font-['Inter'] font-medium text-lg leading-[23px] flex-grow ${isLoading && currentField === 'headline1'
                                        ? 'bg-gray-300 cursor-not-allowed'
                                        : 'bg-black/5 hover:bg-black/10'
                                        }`}
                                >
                                    Regenerate
                                </button>
                                <button
                                    onClick={() => handleEditClick('headline1')}
                                    disabled={isLoading && currentField === 'headline1'}
                                    className={`px-6 py-2 rounded-lg font-['Inter'] font-medium text-lg leading-[23px] text-white flex-grow ${isLoading && currentField === 'headline1'
                                        ? 'bg-gray-400 cursor-not-allowed'
                                        : 'bg-[#6277FF] hover:bg-[#5366e0]'
                                        }`}
                                >
                                    Edit
                                </button>
                            </div>
                        </>
                    )}

                    {editingField === 'headline2' ? (
                        <div className="flex flex-col gap-2">
                            <textarea
                                value={tempText}
                                onChange={(e) => setTempText(e.target.value)}
                                className="border p-2 rounded"
                            />
                            <div className="flex gap-2">
                                <button
                                    onClick={() => handleSaveEdit('headline2')}
                                    className="px-4 py-1.5 bg-green-500 text-white rounded text-sm"
                                >
                                    Save
                                </button>
                                <button
                                    onClick={handleCancelEdit}
                                    className="px-4 py-1.5 bg-gray-300 rounded text-sm"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            {isLoading && currentField === 'headline2' ? (
                                <LoadingAnimation />
                            ) : (
                                <p className="font-['Inter'] font-normal text-xl leading-[150%] text-[#4A4D4F]">
                                    {content.headline2 || <span className="text-gray-400">Enter headline 2</span>}
                                </p>
                            )}
                            <div className="flex gap-2.5 w-[331px]">
                                <button
                                    onClick={() => handleRegenerate('headline2')}
                                    disabled={isLoading && currentField === 'headline2'}
                                    className={`px-6 py-2 rounded-lg font-['Inter'] font-medium text-lg leading-[23px] flex-grow ${isLoading && currentField === 'headline2'
                                        ? 'bg-gray-300 cursor-not-allowed'
                                        : 'bg-black/5 hover:bg-black/10'
                                        }`}
                                >
                                    Regenerate
                                </button>
                                <button
                                    onClick={() => handleEditClick('headline2')}
                                    disabled={isLoading && currentField === 'headline2'}
                                    className={`px-6 py-2 rounded-lg font-['Inter'] font-medium text-lg leading-[23px] text-white flex-grow ${isLoading && currentField === 'headline2'
                                        ? 'bg-gray-400 cursor-not-allowed'
                                        : 'bg-[#6277FF] hover:bg-[#5366e0]'
                                        }`}
                                >
                                    Edit
                                </button>
                            </div>
                        </>
                    )}
                </div>

                {/* Primary Text Section */}
                <div className="flex flex-col gap-3">
                    <h3 className="font-['Inter'] font-semibold text-xl leading-[150%]">
                        Primary text
                    </h3>

                    {editingField === 'primaryText' ? (
                        <div className="flex flex-col gap-2">
                            <textarea
                                value={tempText}
                                onChange={(e) => setTempText(e.target.value)}
                                className="border p-2 rounded h-32"
                            />
                            <div className="flex gap-2">
                                <button
                                    onClick={() => handleSaveEdit('primaryText')}
                                    className="px-4 py-1.5 bg-green-500 text-white rounded text-sm"
                                >
                                    Save
                                </button>
                                <button
                                    onClick={handleCancelEdit}
                                    className="px-4 py-1.5 bg-gray-300 rounded text-sm"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            {isLoading && currentField === 'primaryText' ? (
                                <LoadingAnimation />
                            ) : (
                                <p className="font-['Inter'] font-normal text-xl leading-[150%] text-[#4A4D4F]">
                                    {content.primaryText || <span className="text-gray-400">Enter primary text</span>}
                                </p>
                            )}
                            <div className="flex gap-2.5 w-[331px]">
                                <button
                                    onClick={() => handleRegenerate('primaryText')}
                                    disabled={isLoading && currentField === 'primaryText'}
                                    className={`px-6 py-2 rounded-lg font-['Inter'] font-medium text-lg leading-[23px] flex-grow ${isLoading && currentField === 'primaryText'
                                        ? 'bg-gray-300 cursor-not-allowed'
                                        : 'bg-black/5 hover:bg-black/10'
                                        }`}
                                >
                                    Regenerate
                                </button>
                                <button
                                    onClick={() => handleEditClick('primaryText')}
                                    disabled={isLoading && currentField === 'primaryText'}
                                    className={`px-6 py-2 rounded-lg font-['Inter'] font-medium text-lg leading-[23px] text-white flex-grow ${isLoading && currentField === 'primaryText'
                                        ? 'bg-gray-400 cursor-not-allowed'
                                        : 'bg-[#6277FF] hover:bg-[#5366e0]'
                                        }`}
                                >
                                    Edit
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default GenerateHeadline;